/** C8 (decision D-12): duplicate GIS device names are disambiguated in DISPLAY
 *  by appending the record number ("O836 · #1234") whenever the same name is
 *  borne by two or more DISTINCT device ids within one result set. The
 *  underlying records are untouched — cleanup is a GIS worklist item counted
 *  by the data-quality "duplicate device names" check. */

export interface DeviceNameRef {
  name: string | null | undefined
  id: number | null | undefined
}

/** The set of names borne by 2+ distinct device ids in `rows`. Repeated rows
 *  for the SAME device (e.g. one device at several arc-flash nodes) do not
 *  count as a collision. Empty names and id-less rows are ignored. */
export function collidingNames(rows: readonly DeviceNameRef[]): Set<string> {
  const idsByName = new Map<string, Set<number>>()
  for (const { name, id } of rows) {
    if (!name || id == null) continue
    const ids = idsByName.get(name) ?? new Set<number>()
    ids.add(id)
    idsByName.set(name, ids)
  }
  const out = new Set<string>()
  for (const [name, ids] of idsByName) {
    if (ids.size > 1) out.add(name)
  }
  return out
}

/** Display label for one row: "<name> · #<id>" when the name collides in-set
 *  (and there is an id to disambiguate with); the plain name otherwise.
 *  Returns null when there is no name, so each table keeps its own fallback
 *  ("Device #1234" in the duty table, the node name in arc flash). */
export function disambiguatedName(
  name: string | null | undefined,
  id: number | null | undefined,
  colliding: ReadonlySet<string>,
): string | null {
  if (!name) return null
  if (id != null && colliding.has(name)) return `${name} · #${id}`
  return name
}
